/**
 * useStudioEngine — the audio core of the Tune4Tune Studio mini-DAW.
 *
 * This is a thin orchestrator that owns the shared state and refs, and
 * composes three focused sub-hooks:
 *   - useStudioTransport → play/pause/stop/seek + rAF tick
 *   - useStudioMic       → ambient monitor, noise check, record/stop, calibration
 *   - useStudioExport    → renderStems (parallel OfflineAudioContext + MP3 workers)
 *
 * The public return shape is unchanged, so all Studio components consume
 * this hook exactly as before.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { buildVocalFxChain, DELAY_MAX_WET, REVERB_MAX_WET, sliceBuffer } from './studio/studioAudioUtils';
import { useStudioTransport } from './studio/useStudioTransport';
import { useStudioMic } from './studio/useStudioMic';
import { useStudioExport } from './studio/useStudioExport';

export function useStudioEngine() {
  // ---- clips + timeline state ------------------------------------------
  const [instrumental, setInstrumental] = useState(null); // {buffer, offset, title, id}
  const [vocal, setVocal] = useState(null);               // {buffer, offset}
  const [isPlaying, setIsPlaying] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [position, setPosition] = useState(0);
  const [loadingTrack, setLoadingTrack] = useState(false);
  const [volumes, setVolumes] = useState({ inst: 0.9, vocal: 1.0 });
  const [mutes, setMutes] = useState({ inst: false, vocal: false });
  // Vocal FX amounts, 0–1 → wet gains scaled by their MAX_WET ceilings.
  const [reverb, setReverbState] = useState(0);
  const reverbRef = useRef(0);
  const [delay, setDelayState] = useState(0);
  const delayRef = useRef(0);
  // Split/cut tool: absolute timeline sec of the armed cut on the instrumental,
  // or null when disarmed. Artist taps left/right of the cut to erase that side.
  const [splitAt, setSplitAt] = useState(null);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const undoStackRef = useRef([]); // snapshots of { instrumental, vocal }
  const redoStackRef = useRef([]); // snapshots to reapply after undo

  // ---- shared audio refs -----------------------------------------------
  const ctxRef = useRef(null);
  const gainsRef = useRef({});
  const sourcesRef = useRef([]);
  const playStartRef = useRef({ ctxTime: 0, pos: 0 });
  const rafRef = useRef(null);
  const playingRef = useRef(false);
  const recordingRef = useRef(false);
  const iosUnlockedRef = useRef(false);

  // Mic-facing refs — created here so the transport's tick and cleanup can
  // reach them without importing the mic hook.
  const analyserRef = useRef(null);
  const analyserBufRef = useRef(null);
  const liveVocalPeaksRef = useRef([]);
  const recordStartPosRef = useRef(0);

  // Live refs so callbacks always see current clips without re-subscribing.
  const clipsRef = useRef({ instrumental: null, vocal: null });
  clipsRef.current = { instrumental, vocal };

  // ---- ctx bootstrap ---------------------------------------------------
  const getCtx = useCallback(() => {
    if (!ctxRef.current) {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      const ctx = new Ctx();
      const instGain = ctx.createGain();
      const vocalGain = ctx.createGain();
      instGain.connect(ctx.destination);
      vocalGain.connect(ctx.destination);
      // Vocal FX sends: post-fader so wet levels track the vocal fader/mute.
      const { reverbWet, delayWet } = buildVocalFxChain(
        ctx, vocalGain, ctx.destination, reverbRef.current, delayRef.current,
      );
      ctxRef.current = ctx;
      gainsRef.current = { inst: instGain, vocal: vocalGain, reverbWet, delayWet };
    }
    return ctxRef.current;
  }, []);

  const setReverb = useCallback((amount) => {
    const v = Math.max(0, Math.min(1, amount));
    reverbRef.current = v;
    setReverbState(v);
    const wet = gainsRef.current.reverbWet;
    if (wet) wet.gain.value = v * REVERB_MAX_WET;
  }, []);

  const setDelay = useCallback((amount) => {
    const v = Math.max(0, Math.min(1, amount));
    delayRef.current = v;
    setDelayState(v);
    const wet = gainsRef.current.delayWet;
    if (wet) wet.gain.value = v * DELAY_MAX_WET;
  }, []);

  // Await ctx.resume() AND, on the first user gesture, play a 1-sample silent
  // buffer to fully unlock the audio session on iOS Safari (otherwise Web Audio
  // stays suspended, ctx.currentTime doesn't advance, and Play appears frozen).
  const ensureCtxReady = useCallback(async () => {
    const ctx = getCtx();
    if (ctx.state === 'suspended' || ctx.state === 'interrupted') {
      try { await ctx.resume(); } catch (e) { /* ignore, gesture required */ }
    }
    if (!iosUnlockedRef.current) {
      try {
        const buf = ctx.createBuffer(1, 1, 22050);
        const src = ctx.createBufferSource();
        src.buffer = buf;
        src.connect(ctx.destination);
        src.start(0);
        iosUnlockedRef.current = true;
      } catch (e) { /* fine */ }
    }
    return ctx;
  }, [getCtx]);

  const duration = Math.max(
    instrumental ? instrumental.offset + instrumental.buffer.duration : 0,
    vocal ? vocal.offset + vocal.buffer.duration : 0,
  );

  // ---- undo / redo -----------------------------------------------------
  const pushUndo = useCallback((snapshot = null) => {
    const { instrumental: inst, vocal: voc } = clipsRef.current;
    undoStackRef.current.push(snapshot || { instrumental: inst, vocal: voc });
    if (undoStackRef.current.length > 20) undoStackRef.current.shift();
    // Any new edit invalidates the redo history — standard DAW behaviour.
    redoStackRef.current = [];
    setCanUndo(true);
    setCanRedo(false);
  }, []);

  // ---- core bundle passed to every sub-hook ---------------------------
  const core = {
    // refs
    ctxRef, gainsRef, sourcesRef, playStartRef, rafRef,
    playingRef, recordingRef, clipsRef,
    analyserRef, analyserBufRef, liveVocalPeaksRef, recordStartPosRef,
    // state + setters
    position, setPosition,
    setIsPlaying, setIsRecording,
    volumes, mutes, reverbRef, delayRef,
    setSplitAt, setVocal, setInstrumental,
    // primitives
    getCtx, ensureCtxReady, pushUndo,
  };

  const transport = useStudioTransport(core);
  const { play, pause, stop, seek } = transport;

  const mic = useStudioMic(core, transport);
  const {
    micError, noiseCheck, runNoiseCheck,
    ambientMonitorActive, getMicLevel, getMicDb,
    startRecording, stopRecording,
    latencyMs, calibration, calibrateLatency, resetCalibration, dismissCalibration, nudgeLatency,
  } = mic;

  const { renderStems } = useStudioExport(core);

  // ---- loading ---------------------------------------------------------
  const decodeToClip = useCallback(async (arrayBuffer) => {
    const ctx = getCtx();
    return await ctx.decodeAudioData(arrayBuffer);
  }, [getCtx]);

  const loadInstrumentalFromUrl = useCallback(async (url, meta = {}) => {
    setLoadingTrack(true);
    try {
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) throw new Error('Could not load track');
      const buffer = await decodeToClip(await res.arrayBuffer());
      pushUndo();
      setSplitAt(null);
      setInstrumental({ buffer, offset: 0, title: meta.title || 'Instrumental', id: meta.id || null });
    } finally {
      setLoadingTrack(false);
    }
  }, [decodeToClip, pushUndo]);

  const loadInstrumentalFromFile = useCallback(async (file) => {
    setLoadingTrack(true);
    try {
      const buffer = await decodeToClip(await file.arrayBuffer());
      pushUndo();
      setInstrumental({ buffer, offset: 0, title: file.name, id: null, localFile: file });
    } finally {
      setLoadingTrack(false);
    }
  }, [decodeToClip, pushUndo]);

  const setInstrumentalOffset = useCallback((offset) => {
    setInstrumental((prev) => (prev ? { ...prev, offset: Math.max(0, offset) } : prev));
  }, []);

  // ---- split / cut tool ------------------------------------------------
  const armSplit = useCallback((absoluteSec) => setSplitAt(absoluteSec), []);
  const cancelSplit = useCallback(() => setSplitAt(null), []);

  // Erase one side of the armed cut by physically slicing the buffer. The
  // kept audio STAYS at its timeline position — erasing the left leaves a
  // silent gap at the start that the artist can drag the clip over. Undo
  // restores the full pre-cut clip.
  const eraseSplitSide = useCallback((side) => {
    const inst = clipsRef.current.instrumental;
    if (!inst || splitAt == null) return;
    const cut = splitAt - inst.offset;
    const dur = inst.buffer.duration;
    if (cut <= 0.05 || cut >= dur - 0.05) { setSplitAt(null); return; }
    const ctx = getCtx();
    pushUndo();
    if (side === 'left') {
      setInstrumental({ ...inst, buffer: sliceBuffer(ctx, inst.buffer, cut, dur), offset: splitAt, edited: true });
    } else {
      setInstrumental({ ...inst, buffer: sliceBuffer(ctx, inst.buffer, 0, cut), edited: true });
    }
    setSplitAt(null);
  }, [splitAt, getCtx, pushUndo]);

  const clearVocal = useCallback(() => {
    if (clipsRef.current.vocal) pushUndo();
    setVocal(null);
  }, [pushUndo, setVocal]);

  const clearInstrumental = useCallback(() => {
    if (clipsRef.current.instrumental) pushUndo();
    setInstrumental(null);
  }, [pushUndo]);

  const undo = useCallback(() => {
    const snap = undoStackRef.current.pop();
    if (!snap) return;
    if (playingRef.current) pause();
    // Save the current state onto the redo stack so it can be reapplied.
    const { instrumental: inst, vocal: voc } = clipsRef.current;
    redoStackRef.current.push({ instrumental: inst, vocal: voc });
    if (redoStackRef.current.length > 20) redoStackRef.current.shift();
    setSplitAt(null);
    setInstrumental(snap.instrumental);
    setVocal(snap.vocal);
    setCanUndo(undoStackRef.current.length > 0);
    setCanRedo(true);
  }, [pause, setVocal]);

  const redo = useCallback(() => {
    const snap = redoStackRef.current.pop();
    if (!snap) return;
    if (playingRef.current) pause();
    // Push current state back onto undo so redo is itself undoable.
    const { instrumental: inst, vocal: voc } = clipsRef.current;
    undoStackRef.current.push({ instrumental: inst, vocal: voc });
    if (undoStackRef.current.length > 20) undoStackRef.current.shift();
    setSplitAt(null);
    setInstrumental(snap.instrumental);
    setVocal(snap.vocal);
    setCanUndo(true);
    setCanRedo(redoStackRef.current.length > 0);
  }, [pause, setVocal]);

  // ---- cleanup on unmount ---------------------------------------------
  // The mic hook stops its own streams. Here we tear down the shared
  // audio graph so the browser releases the AudioContext.
  useEffect(() => () => {
    sourcesRef.current.forEach((s) => { try { s.stop(); } catch (e) { /* noop */ } });
    sourcesRef.current = [];
    cancelAnimationFrame(rafRef.current);
    ctxRef.current?.close().catch(() => {});
  }, []);

  return {
    instrumental, vocal, isPlaying, isRecording, position, duration, loadingTrack,
    volumes, mutes, micError,
    noiseCheck, runNoiseCheck,
    canUndo, undo, canRedo, redo, pushUndo,
    loadInstrumentalFromUrl, loadInstrumentalFromFile, setInstrumentalOffset,
    splitAt, armSplit, cancelSplit, eraseSplitSide,
    clearVocal, clearInstrumental,
    play, pause, stop, seek,
    startRecording, stopRecording,
    setVolumes, setMutes,
    reverb, setReverb,
    delay, setDelay,
    renderStems,
    // latency calibration
    latencyMs, calibration, calibrateLatency, resetCalibration, dismissCalibration, nudgeLatency,
    // live-recording waveform (read by Timeline on each animation tick)
    liveVocalPeaksRef,
    recordStartPosRef,
    getMicLevel,
    getMicDb,
    // ambient monitor — LevelMeter reads this to stay live between takes
    ambientMonitorActive,
  };
}
